import * as React from "react";
import { Select as SelectPrimitive } from "radix-ui";

import { cn } from "@shared/lib/cn";
import { IconCheck, IconChevronsUpDown } from "@shared/ui/icons";
import { GrowLayer } from "./grow-layer";
import { MenuPlate } from "./menu-plate";

/** Высота поля — S набора; на неё же список наезжает на поле, вырастая из него. */
const FIELD = 36;

/*
 * Выбор из списка.
 *
 * Единственный орган, где переход на Radix даёт не только единообразие:
 * у нативного `<select>` выпадающий список рисует система, и Nocturne на него
 * не распространяется вовсе — тёмный интерфейс раскрывался светлым списком с
 * чужими отступами и чужим шрифтом.
 *
 * Значки берутся из `ui/icons`, а не из `lucide-react` напрямую: размер и
 * толщина линии настроены там под плотность системы.
 *
 * Поле повторяет `.input`, список — `.menu`: это те же два вида, что уже
 * есть в системе, и третьего заводить не за чем.
 */
const Select = SelectPrimitive.Root;
const SelectGroup = SelectPrimitive.Group;
const SelectValue = SelectPrimitive.Value;

/**
 * Поле списка выбора — того же вида, что кнопка `secondary` (`tone-btn`):
 * материал панелей, при нажатии набухает. Пока список открыт, поле прячется:
 * список вырастает из него и лежит ровно поверх, а закрываясь, уходит
 * обратно — тогда поле и проявляется (правило `data-slot="select-trigger"` в
 * `ui.css`).
 */
function SelectTrigger({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Trigger>) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-variant="secondary"
      className={cn(
        "tone-btn flex w-full items-center justify-between gap-2 bg-transparent",
        "h-[36px] pr-[12px] pl-[16px]",
        "rounded-[var(--radius-pill)] border-0",
        "text-[14px]/[1.4] font-medium text-foreground text-left",
        "cursor-pointer outline-none",
        "focus-visible:border-primary focus-visible:outline-offset-0",
        "disabled:cursor-not-allowed disabled:opacity-55",
        className,
      )}
      {...props}
    >
      <span className="lui-select-value">{children}</span>
      <SelectPrimitive.Icon asChild>
        <IconChevronsUpDown size={15} className="shrink-0 text-muted-foreground" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

/**
 * Список вырастает из поля (`GrowLayer`, материал «Почты»). Он ложится ровно
 * поверх поля (сдвиг на его высоту внутрь), а у нижнего края экрана Radix сам
 * переворачивает его вверх — подложка тогда растёт от низа поля.
 */
function SelectContent({
  className,
  children,
  position = "popper",
  sideOffset = -FIELD,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        position={position}
        sideOffset={sideOffset}
        /* Тот же слой, что у меню: порядок задаёт порядок в DOM. */
        className={cn(
          "z-40 flex flex-col p-[6px] rounded-[18px] pop-grow",
          "min-w-[var(--radix-select-trigger-width)]",
          "max-h-[var(--radix-select-content-available-height)]",
          className,
        )}
        {...props}
      >
        <GrowLayer />
        {/* Подсветка — внутри прокручиваемого списка: она едет вместе с пунктами. */}
        <SelectPrimitive.Viewport className="relative flex flex-col">
          <MenuPlate />
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "relative flex h-[36px] w-full shrink-0 cursor-pointer items-center gap-[8px] rounded-[var(--radius-pill)] pr-[14px] pl-[8px]",
        "text-[13.5px] font-medium text-foreground outline-none select-none",
        "data-[disabled]:cursor-default data-[disabled]:text-muted-foreground",
        className,
      )}
      {...props}
    >
      {/*
       * Место под галочку держат все пункты, а не только отмеченный: иначе
       * выбор другого сдвигает подписи, и глаз ловит движение вместо смысла.
       * Тем же правилом живёт меню.
       */}
      <span aria-hidden className="inline-flex w-[14px] shrink-0 justify-center text-bright">
        <SelectPrimitive.ItemIndicator>
          <IconCheck size={12} />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

export { Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectItem };
